// frontend/src/admin/pages/AdminOrgDetailPage.jsx
import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import toast from "react-hot-toast";

import {
  getOrg,
  overrideOrgPlan,
  suspendOrg,
  unsuspendOrg,
  impersonateOrg,
  deleteOrg,
} from "../api";

const PLANS = ["Free", "Starter", "Pro", "Business"];
/** Where the main app lives (impersonation opens it in a new tab). */
const APP_ORIGIN = import.meta.env.VITE_APP_ORIGIN || window.location.origin;

function fmtDate(s) {
  return s ? new Date(s).toLocaleString() : "—";
}

function Section({ title, children, actions }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 mb-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-slate-700">{title}</h2>
        {actions}
      </div>
      {children}
    </div>
  );
}

function AdminOrgDetailPage() {
  const { orgId } = useParams();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [planChoice, setPlanChoice] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getOrg(orgId);
      setOrg(data);
      setPlanChoice(data.plan_name ?? "Free");
      setError(null);
    } catch {
      setError("Failed to load organisation.");
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    load();
  }, [load]);

  const onOverridePlan = async () => {
    setBusy(true);
    try {
      await overrideOrgPlan(orgId, planChoice);
      toast.success(`Plan changed to ${planChoice}.`);
      await load();
    } catch {
      toast.error("Failed to change plan.");
    } finally {
      setBusy(false);
    }
  };

  const onToggleSuspend = async () => {
    // Suspending blocks the org's API access — confirm first. Unsuspending
    // only restores access, so it needs no guard.
    if (
      !org.suspended_at &&
      !window.confirm(
        `Suspend "${org.name}"? Members lose API access immediately (QR scans still redirect).`,
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      if (org.suspended_at) {
        await unsuspendOrg(orgId);
        toast.success("Organisation unsuspended.");
      } else {
        await suspendOrg(orgId);
        toast.success("Organisation suspended.");
      }
      await load();
    } catch {
      toast.error("Action failed.");
    } finally {
      setBusy(false);
    }
  };

  const onImpersonate = async () => {
    const reason = window.prompt(
      "Reason for impersonating this org (recorded in the audit log):",
    );
    if (!reason) return; // cancelled or empty → abort
    setBusy(true);
    try {
      const res = await impersonateOrg(orgId, reason);
      // Open the main app in a new tab with the short-lived token in the URL;
      // the main app picks it up and enters impersonation mode.
      const url = `${APP_ORIGIN}/?impersonation_token=${encodeURIComponent(
        res.access_token,
      )}`;
      window.open(url, "_blank", "noopener");
      toast.success("Impersonation session opened in a new tab (15 min).");
    } catch {
      toast.error("Could not start impersonation.");
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    const confirmation = window.prompt(
      `This soft-deletes the org. Type its slug "${org.slug}" to confirm:`,
    );
    if (!confirmation) return;
    setBusy(true);
    try {
      await deleteOrg(orgId, confirmation);
      toast.success("Organisation deleted.");
      await load();
    } catch {
      toast.error("Delete failed (slug must match exactly).");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <p className="text-sm text-slate-500">Loading…</p>;
  if (error || !org)
    return <p className="text-sm text-red-600">{error ?? "Not found."}</p>;

  return (
    <div className="max-w-3xl">
      <Link
        to="/admin/orgs"
        className="text-sm text-indigo-600 hover:text-indigo-500"
      >
        ← All organisations
      </Link>

      <div className="flex items-center gap-3 mt-2 mb-5">
        <h1 className="text-xl font-semibold text-slate-900">{org.name}</h1>
        {org.suspended_at && (
          <span className="text-[10px] font-bold uppercase text-amber-700 bg-amber-100 rounded px-1.5 py-0.5">
            Suspended
          </span>
        )}
        {org.white_account && (
          <span className="text-[10px] font-bold uppercase text-emerald-700 bg-emerald-100 rounded px-1.5 py-0.5">
            White account
          </span>
        )}
      </div>

      {/* Org info + plan override */}
      <Section title="Organisation">
        <dl className="grid grid-cols-2 gap-y-2 text-sm">
          <dt className="text-slate-500">Slug</dt>
          <dd className="text-slate-900">{org.slug}</dd>
          <dt className="text-slate-500">Created</dt>
          <dd className="text-slate-900">{fmtDate(org.created_at)}</dd>
          <dt className="text-slate-500">Current plan</dt>
          <dd className="text-slate-900">{org.plan_name ?? "—"}</dd>
        </dl>
        <div className="flex items-center gap-2 mt-4">
          <select
            value={planChoice}
            onChange={(e) => setPlanChoice(e.target.value)}
            aria-label="Override plan"
            className="px-3 py-2 border border-slate-300 rounded-md text-sm"
          >
            {PLANS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={busy || planChoice === org.plan_name}
            onClick={onOverridePlan}
            className="px-3 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-500 disabled:opacity-40"
          >
            Override Plan
          </button>
        </div>
      </Section>

      {/* Usage */}
      <Section title="Usage">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-slate-500">Codes</div>
            <div className="text-2xl font-bold text-slate-900">
              {org.usage?.code_count ?? 0}
            </div>
          </div>
          <div>
            <div className="text-slate-500">Scans (30d)</div>
            <div className="text-2xl font-bold text-slate-900">
              {org.usage?.scan_count_30d ?? 0}
            </div>
          </div>
        </div>
      </Section>

      {/* Billing */}
      <Section title="Billing">
        <dl className="grid grid-cols-2 gap-y-2 text-sm">
          <dt className="text-slate-500">Status</dt>
          <dd className="text-slate-900">{org.billing?.status ?? "none"}</dd>
          <dt className="text-slate-500">Stripe customer</dt>
          <dd className="text-slate-900">
            {org.billing?.stripe_customer_id ?? "—"}
          </dd>
        </dl>
      </Section>

      {/* Members */}
      <Section title={`Members (${org.members?.length ?? 0})`}>
        <table className="w-full text-sm">
          <tbody className="divide-y divide-slate-100">
            {(org.members ?? []).map((m) => (
              <tr key={m.user_id}>
                <td className="py-1.5 text-slate-900">{m.email}</td>
                <td className="py-1.5 text-slate-500">{m.role}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      {/* Actions */}
      <Section title="Actions">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={onToggleSuspend}
            className="px-3 py-2 border border-amber-300 text-amber-700 rounded-md text-sm font-medium hover:bg-amber-50 disabled:opacity-40"
          >
            {org.suspended_at ? "Unsuspend" : "Suspend"}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onImpersonate}
            className="px-3 py-2 border border-indigo-300 text-indigo-700 rounded-md text-sm font-medium hover:bg-indigo-50 disabled:opacity-40"
          >
            Impersonate
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onDelete}
            className="px-3 py-2 border border-red-300 text-red-700 rounded-md text-sm font-medium hover:bg-red-50 disabled:opacity-40"
          >
            Delete
          </button>
        </div>
      </Section>
    </div>
  );
}

export default AdminOrgDetailPage;
